import type { AgentDto } from '@af/shared';
import { Link } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { AgentOrb } from '@/components/ui/agent-orb';
import { useAgentClick } from '@/lib/agent-select';
import { type AgentLive, isWaitingRun, type LiveStatus } from '@/lib/live-agents';
import { cn } from '@/lib/utils';
import { AgentHoverCard } from './AgentHoverCard';
import { activityText } from './LiveOverview';

export interface OfficeRoom {
  id: string;
  /** null = the executive room (top-level agents and reports without a team of their own). */
  lead: AgentDto | null;
  members: AgentDto[];
}

/**
 * Pure: group agents into rooms by the manager hierarchy. Every direct report of a top-level agent
 * that manages others gets a team room with its whole subtree; everyone else sits in "Executive".
 */
export function officeRooms(agents: AgentDto[]): OfficeRoom[] {
  const ids = new Set(agents.map((a) => a.id));
  const parentOf = (a: AgentDto) => (a.managerAgentId && ids.has(a.managerAgentId) ? a.managerAgentId : null);
  const kids = new Map<string | null, AgentDto[]>();
  for (const a of agents) kids.set(parentOf(a), [...(kids.get(parentOf(a)) ?? []), a]);
  const subtree = (id: string, seen = new Set<string>()): AgentDto[] =>
    (kids.get(id) ?? []).flatMap((c) => {
      if (seen.has(c.id)) return [];
      seen.add(c.id);
      return [c, ...subtree(c.id, seen)];
    });

  const roots = kids.get(null) ?? [];
  const exec: AgentDto[] = [...roots];
  const teams: OfficeRoom[] = [];
  for (const r of roots)
    for (const c of kids.get(r.id) ?? []) {
      if ((kids.get(c.id) ?? []).length) teams.push({ id: c.id, lead: c, members: [c, ...subtree(c.id)] });
      else exec.push(c);
    }
  // Agents in a cycle (no root) still need a desk.
  const placed = new Set([...exec, ...teams.flatMap((t) => t.members)].map((a) => a.id));
  for (const a of agents) if (!placed.has(a.id)) exec.push(a);
  return [{ id: 'exec', lead: null, members: exec }, ...teams].filter((r) => r.members.length);
}

const RING: Record<LiveStatus, string> = {
  running: 'var(--claude)',
  approval: 'var(--warn)',
  idle: 'var(--border-strong)',
  paused: 'var(--border)',
  failed: 'var(--danger)',
};

function Desk({ live, reserveActivity }: { live: AgentLive; reserveActivity: boolean }) {
  const { t } = useTranslation();
  const { agent, status, snippet, pending } = live;
  const working = status === 'running' && !isWaitingRun(live.run);
  const onAgent = useAgentClick(agent.id);
  const label = `${agent.name}, ${agent.roleTitle} — ${t(`ops.status.${status}`)}${snippet && working ? `: ${snippet}` : ''}`;
  return (
    <li className="relative min-w-0">
      <AgentHoverCard live={live}>
        <Link
          to="/agents/$id"
          params={{ id: agent.id }}
          onClick={onAgent}
          aria-label={label}
          data-testid={`desk-${agent.slug}`}
          data-status={status}
          className={cn(
            'group relative flex w-full min-w-0 flex-col items-center rounded-xl px-2 pb-3 transition-colors hover:bg-hover',
            reserveActivity ? 'pt-12' : 'pt-3',
            status === 'paused' && 'opacity-60',
          )}
        >
          {status === 'running' || status === 'approval' ? (
            <span
              aria-hidden
              className="absolute top-0 left-1/2 w-[calc(100%-1rem)] max-w-48 -translate-x-1/2 rounded-lg border border-border bg-bg px-2 py-1 text-left font-mono text-[10.5px] leading-snug text-fg-2 shadow-soft after:absolute after:-bottom-[5px] after:left-1/2 after:h-2.5 after:w-2.5 after:-translate-x-1/2 after:rotate-45 after:border-r after:border-b after:border-border after:bg-bg"
            >
              <span className="line-clamp-2 break-all">
                {status === 'approval'
                  ? t('ops.status.approval')
                  : !working
                    ? activityText(t, live)
                    : (snippet ?? (
                        <span className="office-typing inline-flex gap-0.5">
                          <span>●</span>
                          <span>●</span>
                          <span>●</span>
                        </span>
                      ))}
              </span>
            </span>
          ) : null}
          <span className="relative">
            <span
              className="office-avatar block rounded-full p-[2px]"
              data-working={working || undefined}
              style={{ boxShadow: `0 0 0 2px ${RING[status]}` }}
            >
              <AgentOrb seed={agent.id} size={30} avatar={agent.avatar} name={agent.name} />
            </span>
            {pending.length ? (
              <span
                title={t('ops.approvalBadge', { count: pending.length })}
                className="absolute -top-1.5 -right-2 inline-flex h-4 min-w-4 items-center justify-center rounded-full bg-warn px-1 font-mono text-[10px] font-semibold text-bg"
              >
                {pending.length}
              </span>
            ) : null}
          </span>
          {/* Top-down desk + monitor (SVG); the screen lights up while the agent works. */}
          <svg width="96" height="34" viewBox="0 0 96 34" aria-hidden className="-mt-1">
            <rect
              x="4"
              y="6"
              width="88"
              height="26"
              rx="5"
              fill="var(--panel-2)"
              stroke="var(--border-strong)"
            />
            <rect
              x="34"
              y="10"
              width="28"
              height="5"
              rx="1.5"
              fill={
                working ? 'var(--accent)' : status === 'approval' ? 'var(--warn)' : 'var(--border-strong)'
              }
              opacity={working || status === 'approval' ? 0.9 : 1}
            />
            <rect x="38" y="20" width="20" height="6" rx="2" fill="var(--hover)" stroke="var(--border)" />
            <circle cx="72" cy="22" r="3" fill="var(--border)" />
          </svg>
          <span className="mt-1 max-w-full break-words text-center text-xs font-medium text-fg">
            {agent.name}
          </span>
          <span className="max-w-full break-words text-center text-[11px] text-muted">{agent.roleTitle}</span>
        </Link>
      </AgentHoverCard>
    </li>
  );
}

/** "Office" view: a 2D top-down floor, one desk per agent, grouped into team rooms. CSS/SVG only. */
export function OfficeView({ list }: { list: AgentLive[] }) {
  const { t } = useTranslation();
  const byId = new Map(list.map((l) => [l.agent.id, l]));
  const rooms = officeRooms(list.map((l) => l.agent));
  return (
    <section
      aria-label={t('ops.office')}
      className="office-floor min-w-0 rounded-2xl border border-border p-3"
    >
      <div className="grid grid-cols-[repeat(auto-fit,minmax(min(100%,18rem),1fr))] items-start gap-3">
        {rooms.map((room) => (
          <section
            key={room.id}
            aria-label={room.lead ? t('ops.room', { name: room.lead.name }) : t('ops.executive')}
            className="office-room min-w-0 rounded-xl border border-border-strong p-3 shadow-soft"
          >
            <h3 className="mb-3 break-words text-xs font-medium tracking-wide text-muted uppercase">
              {room.lead ? t('ops.room', { name: room.lead.name }) : t('ops.executive')}
              <span className="ml-1.5 font-mono text-faint normal-case">{room.members.length}</span>
            </h3>
            <ul className="grid grid-cols-[repeat(auto-fit,minmax(min(100%,8.25rem),1fr))] items-start gap-3">
              {room.members.map((a) => {
                const l = byId.get(a.id);
                return l ? (
                  <Desk
                    key={a.id}
                    live={l}
                    reserveActivity={room.members.some((member) => {
                      const status = byId.get(member.id)?.status;
                      return status === 'running' || status === 'approval';
                    })}
                  />
                ) : null;
              })}
            </ul>
          </section>
        ))}
      </div>
    </section>
  );
}
